// The customer surface follows the operating system; the review console does
// not. Both are deliberate, and they are different decisions for different
// audiences.
//
// theme.ts says light is the baseline and prefers-color-scheme is deliberately
// ignored "per the owner's call". That call was about the REVIEW CONSOLE, an
// internal tool with its own toggle in the top bar. The customer surface
// renders outside that chrome on purpose, so it inherits the decision without
// inheriting the control: a practice owner whose machine is in dark mode gets a
// white page and no way to change it, while a full dark palette sits in
// tokens.css that the contrast gate tests in both themes and no customer can
// ever see.
//
// So: on this surface only, and only when the person has expressed no
// preference of their own, the OS decides. A stored preference always wins —
// including for someone who holds both an attorney and a customer grant, whose
// toggle must not be silently overridden every time they change surfaces.
//
// The attribute is restored on unmount so navigating back to the review console
// leaves its baseline exactly as it was.

import { useEffect } from 'react';
import { syncThemeColor, withoutTransitions } from '../theme';

const STORED_KEY = 'verityrx.theme';

function hasStoredPreference(): boolean {
  try {
    const v = localStorage.getItem(STORED_KEY);
    return v === 'dark' || v === 'light';
  } catch {
    // Private mode: no preference is recorded, so the OS decides.
    return false;
  }
}

export function useSurfaceTheme(): void {
  useEffect(() => {
    if (hasStoredPreference()) return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const had = document.documentElement.dataset.theme;

    const apply = () => withoutTransitions(() => {
      if (mq.matches) document.documentElement.dataset.theme = 'dark';
      else delete document.documentElement.dataset.theme;
      syncThemeColor();
    });
    apply();
    mq.addEventListener('change', apply);

    return () => {
      mq.removeEventListener('change', apply);
      if (had) document.documentElement.dataset.theme = had;
      else delete document.documentElement.dataset.theme;
      syncThemeColor();
    };
  }, []);
}
